/**
 * TanStackTable - Wrapper component using @tanstack/react-table
 *
 * This is a pilot implementation for gradual migration from the custom Table component.
 * It accepts the same column format as the existing Table component for compatibility.
 *
 * Pilot tables:
 * - PraetorianUsersTable
 * - CustomerUsersTable
 * - Access Logs table
 */
import {
  flexRender,
  getCoreRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';

import { adaptColumnsToTanStack } from '@/components/table/columnAdapter';
import { Columns } from '@/components/table/types';
import { NoData } from '@/components/ui/NoData';
import { cn } from '@/utils/classname';

interface TanStackTableProps<TData> {
  /** Unique table name for identification */
  name: string;
  /** Column definitions (same format as existing Table component) */
  columns: Columns<TData>;
  /** Data array to display */
  data: TData[];
  /** Loading/error status */
  status: 'error' | 'success' | 'pending';
  /** Error object if status is error */
  error?: Error | null;
  /** Row selection configuration */
  selection?: {
    /** Currently selected row keys */
    value?: string[];
    /** Callback when selection changes */
    onChange?: (value: string[]) => void;
    /** Hide selection column */
    hide?: boolean;
  };
  /** Empty state configuration */
  noData?: {
    title?: string;
    description?: string;
    icon?: React.ReactNode;
    action?: React.ReactNode;
    styleType?: 'text' | 'textWithIcon';
  };
  /** Number of skeleton rows to show while loading */
  loadingRowCount?: number;
  /** Custom class name for the table */
  className?: string;
  /** Optional click handler for rows */
  onRowClick?: (row: TData) => void;
  /** Optional function to get custom class name for each row */
  rowClassName?: (row: TData) => string | undefined;
}

/**
 * Loading skeleton row component
 */
function SkeletonRow({ columnCount }: { columnCount: number }) {
  return (
    <tr className="border-chariot-stroke border-b">
      {Array.from({ length: columnCount }).map((_, index) => (
        <td key={index} className="px-4 py-3">
          <div className="bg-chariot-background-secondary h-4 w-full animate-pulse rounded" />
        </td>
      ))}
    </tr>
  );
}

export function TanStackTable<TData>({
  name,
  columns,
  data,
  status,
  error,
  selection,
  noData,
  loadingRowCount = 5,
  className,
  onRowClick,
  rowClassName,
}: TanStackTableProps<TData>) {
  // Internal row selection state (used if no controlled selection)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Convert custom columns to TanStack format
  const tanstackColumns = useMemo(
    () => adaptColumnsToTanStack(columns),
    [columns]
  );

  // Derive controlled selection from props (React Compiler optimizes automatically)
  const controlledSelection = selection?.value
    ? selection.value.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {} as RowSelectionState)
    : undefined;

  // Get current selection (controlled or uncontrolled)
  const currentSelection = controlledSelection ?? rowSelection;

  // Get row ID from data (use 'key' field or index)
  const getRowId = (row: TData, index: number): string => {
    const rowData = row as Record<string, unknown>;
    const key = rowData.key;
    return typeof key === 'string' ? key : String(index);
  };

  // Create TanStack Table instance
  const table = useReactTable({
    data,
    columns: tanstackColumns,
    state: {
      rowSelection: currentSelection,
    },
    onRowSelectionChange: updater => {
      // Apply updater to CURRENT selection (not empty internal state)
      const newSelection =
        typeof updater === 'function' ? updater(currentSelection) : updater;

      // Update internal state only if uncontrolled
      if (!selection?.value) {
        setRowSelection(newSelection);
      }

      // Notify parent if controlled
      if (selection?.onChange) {
        const selectedKeys = Object.keys(newSelection).filter(
          key => newSelection[key]
        );
        selection.onChange(selectedKeys);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    enableRowSelection: !selection?.hide,
  });

  // Render loading state
  if (status === 'pending') {
    return (
      <div className={cn('w-full overflow-auto', className)}>
        <table
          className="w-full border-collapse"
          data-testid={`table-${name}-loading`}
        >
          <thead className="bg-chariot-background-secondary">
            <tr>
              {columns.map((col, index) => (
                <th
                  key={String(col.id) || index}
                  className="text-chariot-text-secondary px-4 py-3 text-left text-sm font-medium"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: loadingRowCount }).map((_, index) => (
              <SkeletonRow key={index} columnCount={columns.length} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Render error state
  if (status === 'error' && error) {
    return (
      <div className="text-chariot-stroke-critical p-4 text-center">
        <p>Error loading data: {error.message}</p>
      </div>
    );
  }

  // Render empty state
  if (data.length === 0) {
    return (
      <NoData
        title={noData?.title || 'No data found'}
        description={noData?.description || 'No results to display.'}
        icon={noData?.icon}
        action={noData?.action}
        styleType={noData?.styleType}
      />
    );
  }

  return (
    <div className={cn('w-full overflow-auto', className)}>
      <table className="w-full border-collapse" data-testid={`table-${name}`}>
        <thead className="bg-chariot-background-secondary sticky top-0 z-10">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const columnDef = header.column.columnDef;
                const width = header.column.getSize();

                return (
                  <th
                    key={header.id}
                    className="text-chariot-text-secondary bg-chariot-background-secondary px-4 py-3 text-left text-sm font-medium"
                    style={{
                      width: width !== 150 ? width : undefined,
                      minWidth: columnDef.minSize,
                      maxWidth: columnDef.maxSize,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(columnDef.header, header.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => {
            const customRowClassName = rowClassName?.(row.original);
            // Always include base classes, custom row class can add hover effects
            const baseClassName =
              'border-chariot-stroke border-t transition-colors';

            return (
              <tr
                key={row.id}
                className={cn(baseClassName, customRowClassName)}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map(cell => {
                  const columnDef = cell.column.columnDef;
                  const width = cell.column.getSize();

                  return (
                    <td
                      key={cell.id}
                      className="text-chariot-text-primary px-4 py-3 text-sm"
                      style={{
                        width: width !== 150 ? width : undefined,
                        minWidth: columnDef.minSize,
                        maxWidth: columnDef.maxSize,
                      }}
                    >
                      {flexRender(columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
